import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import styles from './dropbox-download-section.module.scss';
import {DownloadStateTypes, type OrderDownloadStateNew} from '@Components/download-panel/download-panel.reducer';
import {useAppSelector} from '@/hooks';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Button, Size, Type} from '@Components/button';
import {saveFileToDropbox} from '@Libraries/dropbox-saver';
import {makeFilenameSafe} from '@Utils/file.util';
import {showEditorNotificationMessageGrowl} from '@Components/poster-editor/library/poster-editor-library';

interface DropboxDownloadSectionProps {
  onClickBack?: () => void;
  onAllFilesSaved?: () => void;
  isOpenedInModal?: boolean;
}

interface SingleDropboxItemProps {
  order: OrderDownloadStateNew;
  showBottomPadding?: boolean;
}

function SingleDropboxItem({order, showBottomPadding = true}: SingleDropboxItemProps): ReactElement {
  const isComplete = order.state === DownloadStateTypes.READY;
  const isError = order.state === DownloadStateTypes.ERROR;

  const name = order.name;
  const fileType = order.fileType;

  return (
    <div className={`flex flex-h-row flex-items-center ${showBottomPadding ? 'spacing-m-b-3' : ''}`}>
      {isComplete ? (
        <Icon
          icon="icon-designs"
          className="spacing-m-r-3"
          size={IconSize.SIZE_ICON_20}
          shape={IconShape.CIRCLE}
          type={IconType.FLAT}
          flatIconType={{
            color: FlatIconColor.NEUTRAL,
            type: FlatIconColorType.DEFAULT,
          }}
        />
      ) : isError ? (
        <Icon icon="icon-exclamation-triangle" className="spacing-m-r-3 content-danger" size={IconSize.SIZE_ICON_20} />
      ) : (
        <CircularProgressLoader classes="spacing-m-r-2 spacing-m-l-0" color={LOADER_COLOR.PRIMARY_DARK} size={LOADER_SIZE.SMALL} />
      )}
      {isComplete ? (
        <Text val={`${name}.${fileType}`} size={TextSize.XSMALL} className="content-body" />
      ) : isError ? (
        <Text val={window.i18next.t('pmwjs_something_went_wrong')} size={TextSize.XSMALL} className="content-danger" />
      ) : (
        <Text
          className="content-body"
          dangerouslySetInnerHTML
          size={TextSize.XSMALL}
          val={window.i18next.t('pmwjs_preparing_design', {
            name: `${name}.${fileType}`,
          })}
        />
      )}
    </div>
  );
}

export function DropboxDownloadSection({onClickBack, isOpenedInModal = false, onAllFilesSaved}: DropboxDownloadSectionProps): ReactElement | null {
  const [isSaving, setIsSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  const orderStateHashmapNew = useAppSelector((state) => state.downloadDialog.orderStateHashmapNew);
  const dropboxProcessingOrderIdKeys = useAppSelector((state) => state.downloadDialog.dropboxProcessingOrderIdKeys);

  const dropboxOrders = dropboxProcessingOrderIdKeys
    .map((key) => orderStateHashmapNew[key])
    .filter((order): order is OrderDownloadStateNew => order !== undefined && order.isDropboxDownload === true);

  if (dropboxOrders.length === 0) {
    return null;
  }

  const allComplete = dropboxOrders.every((order) => order.state === DownloadStateTypes.READY);
  const hasError = dropboxOrders.some((order) => order.state === DownloadStateTypes.ERROR);
  const readyOrders = dropboxOrders.filter((order) => order.state === DownloadStateTypes.READY);

  const getHeading = (): string => {
    if (hasError) {
      return window.i18next.t('pmwjs_something_went_wrong');
    }
    if (allComplete) {
      return window.i18next.t('pmwjs_save_to_dropbox');
    }
    return window.i18next.t('pmwjs_preparing_for_dropbox');
  };

  const saveAllToDropbox = async (): Promise<void> => {
    if (isSaving || readyOrders.length === 0) return;

    setIsSaving(true);
    setSavedCount(0);

    for (const order of readyOrders) {
      if (order.state !== DownloadStateTypes.READY || !order.downloadURL) continue;
      const downloadFilename = `${makeFilenameSafe(order.name, 'poster')}.${order.fileType}`;

      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        void saveFileToDropbox(order.downloadURL, downloadFilename, {
          onSuccess: () => {
            setSavedCount((prev) => prev + 1);
            resolve();
          },
          onError: (err) => {
            console.error('Dropbox save error', err);
            resolve();
          },
          onCancel: () => {
            resolve();
          },
        });
      });
    }

    showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_saved'));
    setIsSaving(false);
    onAllFilesSaved?.();
  };

  const getButtonText = (): string => {
    if (isSaving) {
      return `${window.i18next.t('pmwjs_saving')} (${savedCount}/${readyOrders.length})`;
    }
    if (readyOrders.length === 1) {
      return window.i18next.t('pmwjs_save_file');
    }
    return window.i18next.t('pmwjs_save_all_files');
  };

  return (
    <div className="spacing-p-4 flex flex-v-row">
      {isOpenedInModal ? null : (
        <div className="flex flex-h-row flex-items-center spacing-m-b-4">
          {
            <Icon
              onClick={(): void => {
                if (onClickBack) {
                  onClickBack();
                }
              }}
              className="spacing-m-r-1"
              icon="icon-arrow-left"
              size={IconSize.SIZE_ICON_16}
              shape={IconShape.SQUARE}
              type={IconType.GHOST}
            />
          }
          <Text className="content-body" size={TextSize.SMALL} bold val={getHeading()} />
        </div>
      )}

      <div className={styles.itemsContainer}>
        {dropboxOrders.map((order, index) => (
          <SingleDropboxItem key={order.orderIdKey} order={order} showBottomPadding={index !== dropboxOrders.length - 1} />
        ))}
      </div>

      <Button
        size={Size.SMALL}
        type={Type.PRIMARY}
        disabled={!allComplete || isSaving || hasError}
        text={getButtonText()}
        customClasses="spacing-m-t-4"
        onClick={(): void => {
          void saveAllToDropbox();
        }}
      />
    </div>
  );
}
